import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, ArrowDown, Calendar, ClipboardList, Check, X } from "lucide-react";
import SEO from "../components/SEO";
import ResourceCard from "../components/ResourceCard";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  DATA — kept close to the JSX for scan-ability. The Home Page follows the
 *  11-section architecture from the July repositioning brief. Every section
 *  obeys the One-Idea Rule™.
 * ------------------------------------------------------------------------- */

const HERO_FLOW = ["See", "Understand", "Choose", "Execute", "Learn"];

const REALITY_CARDS = [
  "Follow-through varies.",
  "Good practices stay isolated.",
  "Data exists, but clarity is missing.",
  "Leadership spends too much time reacting.",
];

const HIDDEN_TRUTH = {
  before: ["Good Intentions", "Inconsistent Execution", "Uneven Student Experience"],
  after: ["Clear Leadership", "Consistent Execution", "Better Student Experience"],
};

const IMAGINE_LINES = [
  "Attendance interventions happen with dependable follow-through.",
  "Behavior expectations are reinforced consistently.",
  "Enrollment follow-up becomes visible and reliable.",
  "Leadership conversations become clearer.",
  "Successful practices spread instead of staying isolated.",
  "The organization learns from week to week.",
];

const METHOD_STAGES = [
  { n: "01", label: "See Reality\u2122", body: "Observe what is actually happening." },
  { n: "02", label: "Understand Reality\u2122", body: "Interpret patterns and meaning." },
  { n: "03", label: "Choose Deliberately\u2122", body: "Focus leadership attention where it matters most." },
  { n: "04", label: "Execute Consistently\u2122", body: "Build dependable organizational habits." },
  { n: "05", label: "Learn Continuously\u2122", body: "Capture learning and improve over time." },
  { n: "06", label: "Multiply Leadership\u2122", body: "Develop people and systems that outlast individuals." },
  { n: "07", label: "Create Alignment\u2122", body: "Where leadership, systems, and execution reinforce one another." },
];

const LOS_NODES = [
  { label: "Leadership Brief\u2122", body: "A weekly view of what leadership needs to see." },
  { label: "Leadership Decision\u2122", body: "Deliberate focus on where attention matters most." },
  { label: "Weekly Action\u2122", body: "Consistent follow-through, built into the week." },
  { label: "Leadership Reflection\u2122", body: "A moment to capture what the week taught you." },
  { label: "Organizational Memory\u2122", body: "Learning that compounds instead of disappearing." },
];

const SYSTEMS = [
  {
    n: "01",
    name: "Attendance Intervention System\u2122",
    challenge: "Attendance concerns are identified but follow-through varies.",
    gap: "Intervention gets started; consistency across a week breaks down.",
    outcome: "Earlier action, more dependable follow-through, more students supported before patterns take hold.",
    path: "/solutions/attendance-intervention-system",
  },
  {
    n: "02",
    name: "Behavior Consistency System\u2122",
    challenge: "Behavior expectations exist; response varies from classroom to classroom.",
    gap: "The same situation gets three different responses in three different rooms.",
    outcome: "A shared, visible response — so intervention timing improves and culture holds together.",
    path: "/solutions/behavior-consistency-system",
  },
  {
    n: "03",
    name: "Enrollment Follow-Through System\u2122",
    challenge: "Interested families begin the enrollment journey; many never finish it.",
    gap: "Communication depends on individual staff capacity instead of a shared process.",
    outcome: "A repeatable follow-through path from first inquiry to completed enrollment.",
    path: "/solutions/enrollment-follow-through-system",
  },
];

const TRANSFORMATION = [
  { before: "Leadership reacts.", after: "Leadership sees clearly." },
  { before: "Execution varies.", after: "Execution becomes visible." },
  { before: "Follow-through is hard to see.", after: "Follow-through improves." },
  { before: "Knowledge disappears.", after: "Learning compounds." },
  { before: "Good practices stay isolated.", after: "Better practices spread." },
];

/* -------------------------------------------------------------------------
 *  Small local components
 * ------------------------------------------------------------------------- */

function FlowRow({ items, testid }) {
  return (
    <div
      className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-0"
      data-testid={testid}
    >
      {items.map((item, i) => (
        <div key={item} className="flex flex-col md:flex-row md:items-center flex-1">
          <div className="flex-1 text-center border border-[#0A192F]/15 bg-[#FDFBF7] px-4 py-5 font-serif-display text-lg text-[#0A192F]">
            {item}
          </div>
          {i < items.length - 1 && (
            <div className="flex md:mx-3 my-1 md:my-0 justify-center" aria-hidden="true">
              <ArrowRight className="hidden md:block w-5 h-5 text-[#38BDF8]" strokeWidth={1.5} />
              <ArrowDown className="md:hidden w-5 h-5 text-[#38BDF8]" strokeWidth={1.5} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------
 *  PAGE
 * ------------------------------------------------------------------------- */

export default function Home() {
  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    }
  }, []);

  return (
    <>
      <SEO
        title="AlignED — A Leadership Operating System™ for K–12"
        description="AlignED helps educational leaders create Consistent Execution™ across their schools and districts — through the AlignED Method™, delivered as software."
      />

      {/* SECTION 1 — HERO */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">
            A Leadership Operating System&trade; for K&ndash;12
          </div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05] text-[#0A192F] max-w-4xl">
            Better leadership. Stronger systems. More consistent student experiences.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            AlignED helps school and district leaders create Consistent Execution&trade; across their organizations &mdash; through the AlignED Method&trade;, delivered as software.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="hero-walkthrough-btn"
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
            </a>
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid="hero-alignment-check-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>

          {/* Quiet leadership-flow diagram */}
          <div className="mt-16 lg:mt-20 max-w-4xl">
            <div className="text-xs uppercase tracking-[0.25em] text-[#52796F] mb-5">
              How leaders move through the work
            </div>
            <FlowRow items={HERO_FLOW} testid="hero-flow" />
          </div>
        </div>
      </section>

      {/* SECTION 2 — YOUR REALITY */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="reality-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Your Reality</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Leadership is harder than it should be.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              Not because people don&apos;t care. Not because the work is unimportant. But because the day-to-day of running a school pulls leaders away from the work only they can do.
            </p>
          </div>

          {/* Leadership Observation — grounded evidence (Evidence Principle™) */}
          <aside
            className="mt-12 max-w-3xl bg-[#FDFBF7] border-l-2 border-[#D4AF37] pl-6 sm:pl-8 py-6 pr-6"
            data-testid="home-leadership-observation"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Leadership Observation</div>
            <p className="mt-4 text-lg text-[#1A1D20] leading-relaxed">
              Across schools and districts, we repeatedly see the same pattern: leaders rarely struggle to identify important work.
            </p>
            <p className="mt-3 text-lg text-[#1A1D20] leading-relaxed">
              The harder challenge is ensuring that important work happens consistently after priorities are established.
            </p>
            <p className="mt-3 font-serif-display text-lg text-[#52796F] leading-relaxed">
              That is where execution begins to drift.
            </p>
          </aside>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REALITY_CARDS.map((c, i) => (
              <div
                key={c}
                className="bg-[#FDFBF7] border border-[#0A192F]/10 p-7 min-h-[140px] flex items-center"
                data-testid={`reality-card-${i}`}
              >
                <p className="font-serif-display text-xl text-[#0A192F] leading-snug">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — THE HIDDEN TRUTH */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="hidden-truth-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The Hidden Truth</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              The problem is not effort. The problem is consistency.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              Caring people and thoughtful initiatives are not enough if execution varies from classroom to classroom, team to team, and week to week. Every student deserves the same standard of care &mdash; not just the students in the rooms where a system happens to be strong that week.
            </p>
          </div>

          <div className="mt-14 space-y-6">
            <div className="border border-[#0A192F]/10 bg-[#F4F1EB]/60 p-8" data-testid="hidden-truth-before">
              <div className="text-xs uppercase tracking-widest text-[#4A5568] mb-5">Without a shared system</div>
              <FlowRow items={HIDDEN_TRUTH.before} testid="hidden-truth-before-flow" />
            </div>

            <div className="border border-[#0A192F] bg-[#0A192F] text-[#FDFBF7] p-8" data-testid="hidden-truth-after">
              <div className="text-xs uppercase tracking-widest text-[#38BDF8] mb-5">With one</div>
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-0">
                {HIDDEN_TRUTH.after.map((item, i) => (
                  <div key={item} className="flex flex-col md:flex-row md:items-center flex-1">
                    <div className="flex-1 text-center border border-[#FDFBF7]/25 px-4 py-5 font-serif-display text-lg text-[#FDFBF7]">
                      {item}
                    </div>
                    {i < HIDDEN_TRUTH.after.length - 1 && (
                      <div className="flex md:mx-3 my-1 md:my-0 justify-center" aria-hidden="true">
                        <ArrowRight className="hidden md:block w-5 h-5 text-[#38BDF8]" strokeWidth={1.5} />
                        <ArrowDown className="md:hidden w-5 h-5 text-[#38BDF8]" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — IMAGINE IF */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="imagine-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Imagine If</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Imagine if improvement became more consistent.
            </h2>
          </div>
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
            {IMAGINE_LINES.map((line, i) => (
              <li
                key={line}
                className="font-serif-display text-xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-5 py-1"
                data-testid={`imagine-line-${i}`}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 5 — THE ALIGNED METHOD */}
      <section id="method-section" className="py-24 bg-[#FDFBF7]" data-testid="method-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The AlignED Method&trade;</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              A better way to lead through complexity.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              A seven-stage progression that helps educational leaders see reality clearly, focus attention where it matters most, and build the organizational habits that turn intention into consistent execution.
            </p>
          </div>

          <ol className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {METHOD_STAGES.map((s) => (
              <li
                key={s.n}
                className="relative bg-[#F4F1EB]/50 border border-[#0A192F]/10 p-7 pt-9"
                data-testid={`method-stage-${s.n}`}
              >
                <span className="absolute -top-4 left-6 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FDFBF7] border-2 border-[#38BDF8] text-[#0A192F] font-serif-display text-base">
                  {s.n}
                </span>
                <div className="font-serif-display text-xl text-[#0A192F] leading-snug">{s.label}</div>
                <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECTION 6 — LEADERSHIP OPERATING SYSTEM */}
      <section className="py-24 bg-[#0A192F] text-[#FDFBF7]" data-testid="los-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">The Leadership Operating System&trade;</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              The Leadership Operating System&trade; for K&ndash;12 execution.
            </h2>
            <p className="mt-6 text-lg text-[#FDFBF7]/85 leading-relaxed">
              The Method comes to life through a small, deliberate set of leadership practices &mdash; each one designed to make consistent execution easier to sustain over time.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="los-workflow">
            {LOS_NODES.map((n, i) => (
              <div key={n.label} className="relative">
                <div className="border border-[#FDFBF7]/20 bg-[#FDFBF7]/[0.04] p-6 h-full">
                  <div className="text-xs uppercase tracking-widest text-[#D4AF37]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-3 font-serif-display text-lg text-[#FDFBF7] leading-snug">
                    {n.label}
                  </div>
                  <p className="mt-3 text-sm text-[#FDFBF7]/75 leading-relaxed">{n.body}</p>
                </div>
                {i < LOS_NODES.length - 1 && (
                  <div
                    className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10"
                    aria-hidden="true"
                  >
                    <ArrowRight className="w-5 h-5 text-[#38BDF8]" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — LEADERSHIP SYSTEMS */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="systems-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Leadership Systems</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Three leadership systems. One operating philosophy.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              The first three applications of the AlignED Method&trade; &mdash; each addressing a place where good intentions most often fail to translate into consistent execution.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SYSTEMS.map((s) => (
              <Link
                key={s.name}
                to={s.path}
                className="group bg-[#FDFBF7] border border-[#0A192F]/10 p-8 flex flex-col hover:border-[#0A192F]/40 transition-colors"
                data-testid={`system-card-${s.n}`}
              >
                <div className="text-xs uppercase tracking-widest text-[#52796F]">System {s.n}</div>
                <h3 className="mt-4 font-serif-display text-2xl text-[#0A192F] leading-snug">{s.name}</h3>
                <div className="mt-6 space-y-4 flex-1">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52796F]">Leadership challenge</div>
                    <p className="mt-1 text-sm text-[#1A1D20] leading-relaxed">{s.challenge}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52796F]">Execution gap</div>
                    <p className="mt-1 text-sm text-[#1A1D20] leading-relaxed">{s.gap}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#52796F]">Outcome</div>
                    <p className="mt-1 text-sm text-[#1A1D20] leading-relaxed">{s.outcome}</p>
                  </div>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start group-hover:gap-3 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — THE TRANSFORMATION */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="transformation-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The Transformation</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              What changes when leadership becomes more consistent?
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#F4F1EB] border border-[#0A192F]/10 p-8" data-testid="transformation-before">
              <div className="text-xs uppercase tracking-widest text-[#4A5568]">Before AlignED</div>
              <ul className="mt-6 space-y-4">
                {TRANSFORMATION.map((row, i) => (
                  <li key={`b-${i}`} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-[#4A5568] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-lg text-[#1A1D20] leading-relaxed">{row.before}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0A192F] text-[#FDFBF7] border border-[#0A192F] p-8" data-testid="transformation-after">
              <div className="text-xs uppercase tracking-widest text-[#38BDF8]">After AlignED</div>
              <ul className="mt-6 space-y-4">
                {TRANSFORMATION.map((row, i) => (
                  <li key={`a-${i}`} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#38BDF8] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span className="font-serif-display text-lg text-[#FDFBF7]/95 leading-relaxed">{row.after}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — KEEP EXPLORING */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="explore-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Keep Exploring</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Choose the next step in your leadership journey.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ResourceCard
              category="Assessment"
              title={<>Alignment Check&trade;</>}
              description="Reflect on visibility, ownership, follow-through, and execution across your organization."
              ctaLabel={<>Take the Alignment Check&trade;</>}
              href={ALIGNMENT_CHECK_URL}
              testid="explore-alignment-check"
            />
            <ResourceCard
              category="Ideas"
              title="Insights"
              description="Explore leadership ideas from the AlignED Method™ on execution, organizational learning, and stronger student experiences."
              ctaLabel="Explore Insights"
              href="/insights"
              testid="explore-insights"
            />
            <ResourceCard
              category="Newsletter"
              title={<>Aligned Weekly&trade;</>}
              description="One thoughtful leadership idea each school week to help educational leaders think more clearly and execute more consistently."
              ctaLabel="Launching Soon"
              badge="Coming July"
              testid="explore-aligned-weekly"
            />
            <ResourceCard
              category="Story"
              title="Why AlignED Exists"
              description="Learn the belief behind AlignED: better leadership builds better systems, and better systems create better student experiences."
              ctaLabel="Read Our Story"
              href="/about#what-we-believe"
              testid="explore-why-aligned"
            />
          </div>
        </div>
      </section>

      {/* SECTION 10 — OUR BELIEF */}
      <section className="py-28 bg-[#0A192F] text-[#FDFBF7]" data-testid="belief-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Our Belief</div>
          <p className="mt-10 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#FDFBF7] leading-[1.15]">
            Better Leadership Builds Better Systems.
          </p>
          <p className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#FDFBF7] leading-[1.15]">
            Better Systems Create Better Student Experiences.
          </p>
          <p className="mt-8 font-serif-display text-2xl sm:text-3xl text-[#D4AF37] leading-[1.2]">
            Every student deserves both.
          </p>
        </div>
      </section>

      {/* SECTION 11 — FINAL CTA */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Start the conversation</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            Whether you&apos;re exploring or ready to move, there&apos;s a next step that fits.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="final-cta-walkthrough-btn"
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
            </a>
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid="final-cta-alignment-check-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
